import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Container, TextField, InputAdornment, Tabs, Tab,
  Grid, Card, CardContent, CardActions, Button, Chip, Skeleton,
} from "@mui/material";
import {
  Search as SearchIcon,
  MenuBook as MenuBookIcon,
  Description as DescriptionIcon,
  Article as ArticleIcon,
  Newspaper as NewspaperIcon,
  School as SchoolIcon,
  Folder as FolderIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { fetchData } from "../../BaseUrl";

// ─── Fallback data ────────────────────────────────────────────────────────────
const FALLBACK_DOCUMENTS = [
  {
    id: 1,
    title: "Student Handbook 2024-25",
    category: "Handbooks",
    description: "Complete guide for students including rules, schedule, and academic policies.",
    version: "v2.1",
    uploadDate: "Aug 2024",
    fileUrl: null,
    type: "handbook",
  },
  {
    id: 2,
    title: "Parent Guidelines",
    category: "Guidelines",
    description: "Important guidelines for parents regarding school policies and communication.",
    version: "v1.3",
    uploadDate: "Sep 2024",
    fileUrl: null,
    type: "guidelines",
  },
  {
    id: 3,
    title: "Academic Calendar 2024-25",
    category: "Curriculum",
    description: "Full academic year schedule including holidays, exam dates, and events.",
    version: "v1.0",
    uploadDate: "Jul 2024",
    fileUrl: null,
    type: "curriculum",
  },
  {
    id: 4,
    title: "Tamil Language Curriculum",
    category: "Curriculum",
    description: "Detailed curriculum guide for Tamil language learning across all Nilai levels.",
    version: "v3.0",
    uploadDate: "Jun 2024",
    fileUrl: null,
    type: "curriculum",
  },
  {
    id: 5,
    title: "Monthly Newsletter - April 2025",
    category: "Newsletters",
    description: "School news, upcoming events, student achievements, and community updates.",
    version: "v1.0",
    uploadDate: "Apr 2025",
    fileUrl: null,
    type: "newsletter",
  },
];

// ─── Categories ───────────────────────────────────────────────────────────────
const CATEGORIES = ["All", "Handbooks", "Guidelines", "Newsletters", "Curriculum", "Others"];

// ─── Category chip colors ─────────────────────────────────────────────────────
const CATEGORY_COLORS = {
  Handbooks:   { bg: "#EAF1F8", color: "#244C77" },
  Guidelines:  { bg: "#e0f2fe", color: "#0369a1" },
  Newsletters: { bg: "#fef3c7", color: "#92400e" },
  Curriculum:  { bg: "#d1fae5", color: "#065f46" },
  Others:      { bg: "#f3f4f6", color: "#374151" },
};

// ─── Document icon picker ─────────────────────────────────────────────────────
const getDocumentIcon = (category) => {
  const iconProps = { sx: { fontSize: 36 } };
  switch (category) {
    case "Handbooks":   return <MenuBookIcon   {...iconProps} sx={{ ...iconProps.sx, color: "#244C77" }} />;
    case "Guidelines":  return <DescriptionIcon {...iconProps} sx={{ ...iconProps.sx, color: "#0369a1" }} />;
    case "Newsletters": return <NewspaperIcon   {...iconProps} sx={{ ...iconProps.sx, color: "#92400e" }} />;
    case "Curriculum":  return <SchoolIcon      {...iconProps} sx={{ ...iconProps.sx, color: "#065f46" }} />;
    case "Others":      return <FolderIcon      {...iconProps} sx={{ ...iconProps.sx, color: "#6B7280" }} />;
    default:            return <ArticleIcon     {...iconProps} sx={{ ...iconProps.sx, color: "#244C77" }} />;
  }
};

// ─── Skeleton Card ────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <Card sx={{ borderRadius: "12px", border: "1px solid #E5E7EB", height: "100%" }}>
    <CardContent>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
        <Skeleton variant="rounded" width={48} height={48} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="80%" height={24} />
          <Skeleton variant="text" width="40%" height={20} />
        </Box>
      </Box>
      <Skeleton variant="text" width="100%" />
      <Skeleton variant="text" width="90%" />
      <Skeleton variant="text" width="60%" />
    </CardContent>
    <CardActions sx={{ px: 2, pb: 2 }}>
      <Skeleton variant="rounded" width={100} height={36} />
    </CardActions>
  </Card>
);

// ─── Document Card ────────────────────────────────────────────────────────────
const DocumentCard = ({ doc }) => {
  const catStyle = CATEGORY_COLORS[doc.category] || CATEGORY_COLORS.Others;

  const handleDownload = () => {
    if (doc.fileUrl) {
      window.open(doc.fileUrl, "_blank");
    } else {
      // Graceful fallback if URL not yet available
      alert("Download link is not available yet. Please check back later.");
    }
  };

  return (
    <Card
      sx={{
        borderRadius: "12px",
        border: "1px solid #E5E7EB",
        backgroundColor: "#fff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        cursor: "default",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        },
      }}
    >
      <CardContent sx={{ flex: 1, p: 3 }}>
        {/* Icon + Title row */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "10px",
              backgroundColor: catStyle.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {getDocumentIcon(doc.category)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{
                color: "#111827",
                lineHeight: 1.3,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {doc.title}
            </Typography>
            <Chip
              label={doc.category}
              size="small"
              sx={{
                mt: 0.5,
                backgroundColor: catStyle.bg,
                color: catStyle.color,
                fontWeight: 600,
                fontSize: 11,
                height: 22,
              }}
            />
          </Box>
        </Box>

        {/* Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            lineHeight: 1.6,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            mb: 2,
          }}
        >
          {doc.description}
        </Typography>

        {/* Meta row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Version: <strong>{doc.version}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {doc.uploadDate}
          </Typography>
        </Box>
      </CardContent>

      <CardActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          sx={{
            backgroundColor: "#244C77",
            textTransform: "none",
            borderRadius: "8px",
            fontWeight: 600,
            fontSize: 13,
            px: 2,
            "&:hover": { backgroundColor: "#244C77" },
            boxShadow: "none",
          }}
        >
          Download
        </Button>
      </CardActions>
    </Card>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Handbooks = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  // ── Load ────────────────────────────────────────────────────────────────────
  const loadDocuments = useCallback(async (category, search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category && category !== "All") params.append("category", category);
      if (search) params.append("search", search);

      const result = await fetchData(`public/handbooks?${params.toString()}`);
      const data = result?.data || result;
      if (Array.isArray(data) && data.length > 0) {
        setDocuments(data);
      } else {
        setDocuments(FALLBACK_DOCUMENTS);
      }
    } catch {
      setDocuments(FALLBACK_DOCUMENTS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocuments(activeCategory, searchQuery);
  }, [loadDocuments, activeCategory, searchQuery]);

  // ── Client-side filter (applied on fallback data) ───────────────────────────
  const filteredDocs = documents.filter((doc) => {
    const matchCategory = activeCategory === "All" || doc.category === activeCategory;
    const matchSearch =
      !searchQuery ||
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc.description && doc.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchCategory && matchSearch;
  });

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F9FAFB" }}>
      {/* ── Hero Section ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #3B4E8C 0%, #244C77 50%, #4A7BA8 100%)",
          py: "80px",
          px: 2,
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            fontWeight={800}
            sx={{ color: "#fff", mb: 2, lineHeight: 1.2, fontSize: { xs: "2rem", md: "2.75rem" } }}
          >
            School Handbooks &amp; Documents
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "rgba(255,255,255,0.85)", fontSize: 18, mb: 5, lineHeight: 1.6 }}
          >
            Browse and download official school documents, handbooks, and guidelines.
          </Typography>

          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder="Search by title or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9CA3AF", fontSize: 22 }} />
                </InputAdornment>
              ),
              sx: {
                backgroundColor: "#fff",
                borderRadius: "12px",
                fontSize: 16,
                "& fieldset": { border: "none" },
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              },
            }}
            sx={{ maxWidth: 600, mx: "auto", display: "block" }}
          />
        </Container>
      </Box>

      {/* ── Main Content ─────────────────────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Category Tabs */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}>
          <Tabs
            value={activeCategory}
            onChange={(_, val) => setActiveCategory(val)}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{
              style: { backgroundColor: "#244C77", height: 3, borderRadius: 2 },
            }}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                color: "#6B7280",
                minWidth: "auto",
                px: 2,
                "&.Mui-selected": { color: "#244C77" },
              },
              borderBottom: "2px solid #E5E7EB",
            }}
          >
            {CATEGORIES.map((cat) => (
              <Tab key={cat} label={cat} value={cat} />
            ))}
          </Tabs>
        </Box>

        {/* Results count */}
        {!loading && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, textAlign: "center" }}
          >
            {filteredDocs.length === 0
              ? "No documents found"
              : `Showing ${filteredDocs.length} document${filteredDocs.length !== 1 ? "s" : ""}`}
          </Typography>
        )}

        {/* Document Grid */}
        {loading ? (
          <Grid container spacing={3}>
            {[...Array(6)].map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <SkeletonCard />
              </Grid>
            ))}
          </Grid>
        ) : filteredDocs.length > 0 ? (
          <Grid container spacing={3}>
            {filteredDocs.map((doc) => (
              <Grid item xs={12} sm={6} md={4} key={doc.id}>
                <DocumentCard doc={doc} />
              </Grid>
            ))}
          </Grid>
        ) : (
          /* Empty State */
          <Box
            sx={{
              textAlign: "center",
              py: 10,
              px: 2,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: "#F3F4F6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <SearchIcon sx={{ fontSize: 36, color: "#9CA3AF" }} />
            </Box>
            <Typography variant="h6" fontWeight={600} color="text.primary" mb={1}>
              No documents found matching your search
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search terms or selecting a different category.
            </Typography>
            <Button
              variant="outlined"
              sx={{
                mt: 3,
                textTransform: "none",
                borderColor: "#244C77",
                color: "#244C77",
                borderRadius: "8px",
                "&:hover": { backgroundColor: "#EAF1F8", borderColor: "#244C77" },
              }}
              onClick={() => { setSearchQuery(""); setActiveCategory("All"); }}
            >
              Clear filters
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Handbooks;
